import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getSectionQuestions,
  deleteSectionQuestion,
  updateSectionQuestion,
} from "../../services/sectionQuestionsService";

const toastCfg = { autoClose: 1000 };

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ─── Edit Modal ─── */
const QuestionModal = ({ show, onHide, onSuccess, editData, section }) => {
  const [question, setQuestion] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && editData) {
      setQuestion(editData.question || "");
    } else {
      setQuestion("");
    }
  }, [show, editData]);

  const handleSubmit = async () => {
    if (!editData?.id) return toast.error("Invalid ID", toastCfg);
    if (!question.trim()) return toast.error("Question cannot be empty", toastCfg);

    setSaving(true);
    try {
      const res = await updateSectionQuestion(section, {
        id: editData.id,
        question: question,
      });

      if (res?.status) {
        toast.success("Updated successfully", toastCfg);
        onSuccess();
        onHide();
      } else {
        toast.error(res?.msg || "Update failed", toastCfg);
      }
    } catch {
      toast.error("Server error", toastCfg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="lg">
      <div style={ms.header}>
        <div>
          <div style={ms.headerTitle}>Modify Question</div>
          <div style={ms.headerSub}>Edit Section {section} polling wording</div>
        </div>
        <button style={ms.closeBtn} onClick={onHide} disabled={saving}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>
      <div style={ms.body}>
        <div style={ms.grid}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={ms.label}>Question Text</label>
            <textarea
              style={{ ...ms.input, minHeight: 120, resize: "vertical" }}
              placeholder="Enter comprehensive question text..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={saving}
              autoFocus
            />
          </div>
        </div>
      </div>
      <div style={ms.footer}>
        <button style={ms.cancelBtn} onClick={onHide} disabled={saving}>Cancel</button>
        <button style={ms.approveBtn} onClick={handleSubmit} disabled={saving}>
          {saving ? <span className="spinner-border spinner-border-sm" /> : "Save Changes"}
        </button>
      </div>
    </Modal>
  );
};

/* ─── Modal Styles ─── */
const ms = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" },
  headerTitle: { fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" },
  headerSub: { fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 },
  closeBtn: { background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" },
  body: { padding: "18px 24px" },
  grid: { display: "grid", gridTemplateColumns: "1fr", gap: "16px 20px" },
  label: { display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { width: "100%", padding: "12px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit" },
  footer: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 24px 20px", borderTop: "1px solid #f1f5f9" },
  cancelBtn: { padding: "8px 18px", background: "#f1f5f9", border: "none", borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 600, color: "#475569", cursor: "pointer" },
  approveBtn: { display: "flex", alignItems: "center", gap: 6, padding: "8px 20px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", minWidth: 100, justifyContent: "center" },
};

/* ─── Page Styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerWrap: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  pageTitle: { margin: 0, fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" },
  pageSub: { margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 8 },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", gap: 10 },
  toolbarLeft: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },
  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: { padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.82rem", color: "#334155", background: "#f8fafc", outline: "none", width: 280, fontFamily: "inherit" },
  clearBtn: { position: "absolute", right: 8, background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0, display: "flex", alignItems: "center" },
  select: { padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.81rem", color: "#475569", background: "#f8fafc", outline: "none", cursor: "pointer", fontFamily: "inherit", minWidth: 120 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: { padding: "10px 14px", background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9", whiteSpace: "nowrap", textAlign: "left" },
  td: { padding: "12px 14px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },
  emptyCell: { padding: "48px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
  pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 8 },
  pgInfo: { fontSize: "0.78rem", color: "#64748b" },
  pgBtns: { display: "flex", gap: 4 },
  pgBtn: { minWidth: 30, height: 30, border: "1px solid #e2e8f0", background: "#fff", borderRadius: 7, cursor: "pointer", fontSize: "0.78rem", color: "#475569", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" },
  pgBtnActive: { background: "linear-gradient(135deg,#16632b,#1da144)", color: "#fff", border: "none", fontWeight: 700 },
};

const ts = {
  editBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 600, minWidth: 64, justifyContent: "center" },
  delBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 600, minWidth: 64, justifyContent: "center" },
};

export const SectionAListLayer = () => {
  const section = "A";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const fetchData = useCallback(async (isAutoRefresh = false) => {
    if (!isAutoRefresh) setLoading(true);
    try {
      const res = await getSectionQuestions(section);
      if (res?.status) {
        setData(res.data || []);
      }
    } catch {
      if (!isAutoRefresh) toast.error("Failed to load Section A", toastCfg);
    } finally {
      if (!isAutoRefresh) setLoading(false);
    }
  }, [section]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      fetchData(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      const res = await deleteSectionQuestion(section, id);
      if (res?.status) {
        toast.success("Deleted successfully", toastCfg);
        fetchData(true);
      }
    } catch {
      toast.error("Operation failed", toastCfg);
    }
  };

  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 60,
      enableSorting: false,
      cell: ({ row }) => <span style={{ color: "#64748b" }}>{row.index + 1 + pagination.pageIndex * pagination.pageSize}</span>,
    },
    {
      accessorKey: "question",
      header: "Question",
      cell: ({ getValue }) => <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.85rem", lineHeight: 1.5 }}>{getValue() || "—"}</div>,
    },
    {
      id: "actions",
      header: "Action",
      size: 160,
      enableSorting: false,
      cell: ({ row }) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button style={ts.editBtn} onClick={() => { setEditData(row.original); setShowModal(true); }}>
            <Icon icon="solar:pen-bold" style={{ fontSize: 13 }} /> Edit
          </button>
          <button style={ts.delBtn} onClick={() => handleDelete(row.original.id)}>
            <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: 13 }} /> Delete
          </button>
        </div>
      ),
    },
  ], [pagination.pageIndex, pagination.pageSize]);

  const table = useReactTable({
    data: data,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, filterValue) => {
      const v = filterValue.toLowerCase();
      return row.original.question?.toLowerCase().includes(v);
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const filteredDataLength = table.getFilteredRowModel().rows.length;

  return (
    <div style={s.root}>
      <div style={s.headerWrap}>
        <div>
          <h4 style={s.pageTitle}>Section A Questions</h4>
          <p style={s.pageSub}>
             Manage database polling questions
            <span style={{ background: "#dcfce7", color: "#16a34a", padding: "2px 8px", borderRadius: 100, fontSize: "0.65rem", fontWeight: 700, marginLeft: 6 }}>
              LIVE SYNC
            </span>
          </p>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search questions keyword…"
                value={globalFilter}
                onChange={(e) => { setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>
          </div>

          <div style={s.toolbarRight}>
            <select style={s.select} value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}>
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", color: "#94a3b8" }}>
                      <span className="spinner-border spinner-border-sm" />
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:document-text-bold-duotone" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No questions found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr key={row.id}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            Showing{" "}
            <strong>{filteredDataLength > 0 ? pageIndex * pageSize + 1 : 0}–{Math.min((pageIndex + 1) * pageSize, filteredDataLength)}</strong>
            {" "}of <strong>{filteredDataLength}</strong> records
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}          disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}            disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const MathMax = Math.max;
              const MathMin = Math.min;
              const pg = MathMax(0, MathMin(pageIndex - 3, pageCount - 7)) + i;
              if (pg >= pageCount) return null;
              return (
                <button key={pg} style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>

      <QuestionModal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        onSuccess={() => fetchData(true)} 
        editData={editData}
        section={section}
      />
    </div>
  );
};